import React, { useEffect, useMemo, useState } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Select,
  Tag,
  Typography,
  Input,
  Row,
  Col,
  Alert,
  Dropdown,
  Modal,
} from 'antd';
import type { MenuProps } from 'antd';
import { DownOutlined, EyeOutlined, PrinterOutlined, ReloadOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useDeliveries, useMarkDeliveryReturned } from '@/hooks/useDelivery';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import type { DeliveryNote } from '@/services/delivery.service';
import { getDeliveryStatusTagColor } from '@/services/delivery.service';
import { CreateDeliveryFromWorkOrderModal } from '@/components/sales/CreateDeliveryFromWorkOrderModal';
import { CreateDeliveryFromSalesOrderWizard } from '@/components/sales/CreateDeliveryFromSalesOrderWizard';
import { CreateDirectDeliveryFromSalesOrderModal } from '@/components/sales/CreateDirectDeliveryFromSalesOrderModal';
import { useAuthStore } from '@/store/authStore';
import DeliveryCompletionModal from '@/components/sales/DeliveryCompletionModal';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { useUsers } from '@/hooks/useUsers';

const DELIVERY_TRANSITION_PERMS = ['growth.delivery.transition', 'growth.delivery.edit', 'growth.order.edit', 'growth.view'] as const;

function userCanTransitionDelivery(user: { is_super_admin?: boolean; permissions?: string[] } | null | undefined): boolean {
  if (!user) return false;
  if (user.is_super_admin) return true;
  const p = user.permissions ?? [];
  return DELIVERY_TRANSITION_PERMS.some((code) => p.includes(code));
}

type DeliveriesLocationState = {
  openDirectFromSo?: boolean;
  salesOrderId?: string;
  customerId?: string;
};

const { Title, Text } = Typography;
const { Option } = Select;

const DeliveriesPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user } = useAuthStore();
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [searchText, setSearchText] = useState('');
  const debouncedSearch = useDebouncedValue(searchText.trim(), 300);
  const [soFilter, setSoFilter] = useState<string>(() => searchParams.get('sales_order_id')?.trim() ?? '');
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [soWizardOpen, setSoWizardOpen] = useState(false);
  const [directCreateModalOpen, setDirectCreateModalOpen] = useState(false);
  const [directInitialSalesOrderId, setDirectInitialSalesOrderId] = useState<string | null>(null);
  const [directInitialCustomerId, setDirectInitialCustomerId] = useState<string | null>(null);
  const [completeDelivery, setCompleteDelivery] = useState<DeliveryNote | null>(null);
  const directDeepLinkHandled = React.useRef(false);

  useEffect(() => {
    const q = searchParams.get('sales_order_id')?.trim();
    if (q) setSoFilter(q);
  }, [searchParams]);

  useEffect(() => {
    if (directDeepLinkHandled.current) return;
    const state = (location.state || {}) as DeliveriesLocationState;
    const wantDirect =
      state.openDirectFromSo === true || searchParams.get('direct') === '1';
    if (!wantDirect) return;
    directDeepLinkHandled.current = true;
    const soId = state.salesOrderId?.trim() || searchParams.get('sales_order_id')?.trim() || '';
    const customerId = state.customerId?.trim() || '';
    setDirectInitialSalesOrderId(soId || null);
    setDirectInitialCustomerId(customerId || null);
    setDirectCreateModalOpen(true);
    if (soId) setSoFilter(soId);
    // Clear one-shot deep-link so refresh/back does not reopen the modal.
    navigate(
      {
        pathname: location.pathname,
        search: soId ? `?sales_order_id=${encodeURIComponent(soId)}` : '',
      },
      { replace: true, state: {} }
    );
  }, [location.state, location.pathname, navigate, searchParams]);

  const markReturned = useMarkDeliveryReturned();
  const transitionBusy = markReturned.isPending;
  const canTransition = userCanTransitionDelivery(user);

  const { data: list = [], isLoading, refetch } = useDeliveries({
    delivery_status: statusFilter || undefined,
    sales_order_id: soFilter || undefined,
    search: debouncedSearch || undefined,
  });
  const { data: users = [] } = useUsers();
  const usernameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const u of users) {
      if (u.id) m.set(u.id, u.username || [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email);
    }
    return m;
  }, [users]);

  const columns: ColumnsType<DeliveryNote> = [
    {
      title: 'DN Number',
      dataIndex: 'delivery_note_number',
      key: 'delivery_note_number',
      fixed: 'left',
      width: 200,
      ellipsis: true,
      onCell: () => ({ style: { overflow: 'hidden' } }),
      render: (val: string, row: DeliveryNote) => (
        <Button
          type="link"
          onClick={() => navigate(`/growth/delivery-notes/${row.id}`)}
          style={{
            padding: 0,
            height: 'auto',
            maxWidth: '100%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            display: 'inline-block',
            verticalAlign: 'bottom',
          }}
          title={val}
        >
          {val}
        </Button>
      ),
    },
    {
      title: 'Doc package',
      dataIndex: 'document_package_status',
      key: 'document_package_status',
      width: 160,
      ellipsis: true,
      onCell: () => ({ style: { overflow: 'hidden' } }),
      render: (v: string | undefined) => {
        const label = v || 'PendingCollection';
        return (
          <Tag
            title={label}
            style={{
              margin: 0,
              maxWidth: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {label}
          </Tag>
        );
      },
    },
    {
      title: 'Customer Order',
      dataIndex: ['salesOrder', 'sales_order_number'],
      key: 'sales_order_number',
      width: 190,
      ellipsis: true,
      onCell: () => ({ style: { overflow: 'hidden' } }),
      render: (val: string, row: DeliveryNote) =>
        val ? (
          <Button
            type="link"
            onClick={() => navigate(`/growth/orders/${row.sales_order_id}`)}
            style={{
              padding: 0,
              height: 'auto',
              maxWidth: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              display: 'inline-block',
              verticalAlign: 'bottom',
            }}
            title={val}
          >
            {val}
          </Button>
        ) : (
          '-'
        ),
    },
    {
      title: 'Invoice',
      key: 'sales_invoice_number',
      width: 190,
      ellipsis: true,
      onCell: () => ({ style: { overflow: 'hidden' } }),
      render: (_: unknown, row: DeliveryNote) => {
        const invoices = row.sales_invoices?.length
          ? row.sales_invoices
          : row.sales_invoice_number
            ? [{ id: '', invoice_number: row.sales_invoice_number }]
            : [];
        if (!invoices.length) return '-';
        return (
          <Space size={4} wrap>
            {invoices.map((inv, idx) => (
              <React.Fragment key={inv.id || inv.invoice_number}>
                {idx > 0 ? <span>,</span> : null}
                {inv.id ? (
                  <Button
                    type="link"
                    onClick={() => navigate(`/growth/invoices/${inv.id}`)}
                    style={{
                      padding: 0,
                      height: 'auto',
                      maxWidth: 160,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      display: 'inline-block',
                      verticalAlign: 'bottom',
                    }}
                    title={inv.invoice_number}
                  >
                    {inv.invoice_number}
                  </Button>
                ) : (
                  <span title={inv.invoice_number}>{inv.invoice_number}</span>
                )}
              </React.Fragment>
            ))}
          </Space>
        );
      },
    },
    {
      title: 'Customer',
      dataIndex: ['customer', 'customer_name'],
      key: 'customer_name',
      width: 180,
      ellipsis: true,
      onCell: () => ({ style: { overflow: 'hidden' } }),
    },
    {
      title: 'L.P.O',
      dataIndex: 'customer_po_number',
      key: 'customer_po_number',
      width: 120,
      ellipsis: true,
    },
    {
      title: 'Delivery Date',
      dataIndex: 'delivery_date',
      key: 'delivery_date',
      width: 120,
      render: (v: string) => (v ? dayjs(v).format('DD MMM YYYY') : '-'),
    },
    {
      title: 'Status',
      dataIndex: 'delivery_status',
      key: 'delivery_status',
      width: 120,
      render: (status: string) => {
        const label = status || 'Prepared';
        return <Tag color={getDeliveryStatusTagColor(status)}>{label}</Tag>;
      },
    },
    {
      title: 'Prepared By',
      dataIndex: 'created_by',
      key: 'prepared_by',
      width: 130,
      ellipsis: true,
      render: (uid: string | null | undefined) => (uid ? usernameById.get(uid) || '-' : '-'),
    },
    {
      title: 'Delivery man',
      key: 'delivery_assignee',
      width: 140,
      ellipsis: true,
      render: (_: unknown, row: DeliveryNote) =>
        row.deliveryAssignee
          ? `${row.deliveryAssignee.first_name}${row.deliveryAssignee.last_name ? ` ${row.deliveryAssignee.last_name}` : ''}`
          : '-',
    },
    {
      title: 'Delivered At',
      dataIndex: 'delivered_complete_at',
      key: 'delivered_complete_at',
      width: 160,
      render: (v: string | undefined) => (v ? dayjs(v).format('DD MMM YYYY HH:mm') : '-'),
    },
    {
      title: 'Actions',
      key: 'actions',
      fixed: 'right',
      width: 260,
      render: (_: unknown, row: DeliveryNote) => {
        const st = row.delivery_status || 'Prepared';
        const stageMenuItems: MenuProps['items'] = [];
        if (canTransition && st === 'Prepared') {
          stageMenuItems.push({
            key: 'in-transit',
            label: 'Mark in transit',
            onClick: () => navigate(`/growth/delivery-notes/${row.id}?stage=in-transit`),
          });
        }
        if (canTransition && st === 'In Transit') {
          stageMenuItems.push(
            {
              key: 'delivered',
              label: 'Mark delivered',
              onClick: () => setCompleteDelivery(row),
            },
            {
              key: 'returned',
              label: 'Mark returned',
              onClick: () =>
                Modal.confirm({
                  title: 'Mark returned?',
                  content: `Shipment for ${row.delivery_note_number} will be marked Returned.`,
                  okText: 'Confirm',
                  onOk: () => markReturned.mutateAsync({ id: row.id }),
                }),
            }
          );
        }
        return (
          <Space>
            <Button
              type="link"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => navigate(`/growth/delivery-notes/${row.id}`)}
            >
              View
            </Button>
            <Dropdown
              menu={{
                items: [
                  {
                    key: 'packing-letterhead',
                    label: 'Packing list (with letterhead)',
                    icon: <PrinterOutlined />,
                    onClick: () =>
                      navigate(
                        `/print/sales/delivery-notes/${row.id}?variant=packing&format=letterhead`
                      ),
                  },
                  {
                    key: 'packing-preprinted',
                    label: 'Packing list (preprinted)',
                    icon: <PrinterOutlined />,
                    onClick: () =>
                      navigate(
                        `/print/sales/delivery-notes/${row.id}?variant=packing&format=preprinted`
                      ),
                  },
                  {
                    key: 'dn-letterhead',
                    label: 'Delivery note (with letterhead)',
                    icon: <PrinterOutlined />,
                    onClick: () =>
                      navigate(
                        `/print/sales/delivery-notes/${row.id}?variant=delivery-note&format=letterhead`
                      ),
                  },
                  {
                    key: 'dn-preprinted',
                    label: 'Delivery note (preprinted)',
                    icon: <PrinterOutlined />,
                    onClick: () =>
                      navigate(
                        `/print/sales/delivery-notes/${row.id}?variant=delivery-note&format=preprinted`
                      ),
                  },
                  {
                    key: 'ci-letterhead',
                    label: 'Commercial invoice (with letterhead)',
                    icon: <PrinterOutlined />,
                    onClick: () =>
                      navigate(
                        `/print/sales/delivery-notes/${row.id}?variant=commercial-invoice&format=letterhead`
                      ),
                  },
                  {
                    key: 'ci-preprinted',
                    label: 'Commercial invoice (preprinted)',
                    icon: <PrinterOutlined />,
                    onClick: () =>
                      navigate(
                        `/print/sales/delivery-notes/${row.id}?variant=commercial-invoice&format=preprinted`
                      ),
                  },
                ],
              }}
              trigger={['click']}
            >
              <Button type="link" size="small" icon={<PrinterOutlined />}>
                Print <DownOutlined />
              </Button>
            </Dropdown>
            {stageMenuItems.length > 0 ? (
              <Dropdown menu={{ items: stageMenuItems }} trigger={['click']} disabled={transitionBusy}>
                <Button type="link" size="small" loading={transitionBusy} icon={<DownOutlined />}>
                  Stage
                </Button>
              </Dropdown>
            ) : null}
          </Space>
        );
      },
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16 }}>
        <div>
          <Title level={2} style={{ margin: 0 }}>Delivery Notes</Title>
          <Text type="secondary" style={{ display: 'block', marginTop: 4 }}>Shipments and delivery note register</Text>
          <Alert
            type="info"
            showIcon
            style={{ marginTop: 16, maxWidth: 720 }}
            message="Delivery note register"
            description="Filter by status or customer order. Open a DN # for detail and print. Table header stays visible while scrolling."
          />
        </div>
        <Space wrap>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setSoWizardOpen(true)}>
            From customer order
          </Button>
          <Button icon={<PlusOutlined />} onClick={() => setCreateModalOpen(true)}>
            From work order
          </Button>
          <Button
            icon={<PlusOutlined />}
            onClick={() => {
              setDirectInitialSalesOrderId(null);
              setDirectInitialCustomerId(null);
              setDirectCreateModalOpen(true);
            }}
          >
            Direct from customer order
          </Button>
        </Space>
      </div>
      <Card>
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col>
            <Input
              placeholder="Search DN #, customer, SO #"
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: 280 }}
              allowClear
            />
          </Col>
          <Col>
            <Select
              placeholder="Status"
              allowClear
              style={{ width: 160 }}
              value={statusFilter || undefined}
              onChange={setStatusFilter}
            >
              <Option value="">All</Option>
              <Option value="Prepared">Prepared</Option>
              <Option value="In Transit">In Transit</Option>
              <Option value="Delivered">Delivered</Option>
              <Option value="Returned">Returned</Option>
            </Select>
          </Col>
          <Col>
            <Input
              placeholder="Customer Order ID (optional)"
              value={soFilter}
              onChange={(e) => setSoFilter(e.target.value)}
              style={{ width: 200 }}
              allowClear
            />
          </Col>
          <Col>
            <Button icon={<ReloadOutlined />} onClick={() => refetch()}>
              Refresh
            </Button>
          </Col>
        </Row>
        <Table
          rowKey="id"
          columns={columns}
          dataSource={list}
          loading={isLoading}
          sticky={{ offsetHeader: 0 }}
          pagination={{
            defaultPageSize: 20,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} delivery notes`,
            pageSizeOptions: ['10', '20', '50', '100'],
          }}
          scroll={{ x: 1780, y: 520 }}
          tableLayout="fixed"
          size="small"
        />
      </Card>
      <CreateDeliveryFromSalesOrderWizard
        open={soWizardOpen}
        onClose={() => setSoWizardOpen(false)}
        onSuccess={(deliveryId) => {
          setSoWizardOpen(false);
          navigate(`/growth/delivery-notes/${deliveryId}`);
        }}
      />
      <CreateDeliveryFromWorkOrderModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSuccess={(deliveryId) => {
          setCreateModalOpen(false);
          navigate(`/growth/delivery-notes/${deliveryId}`);
        }}
      />
      <CreateDirectDeliveryFromSalesOrderModal
        open={directCreateModalOpen}
        initialSalesOrderId={directInitialSalesOrderId}
        initialCustomerId={directInitialCustomerId}
        onClose={() => {
          setDirectCreateModalOpen(false);
          setDirectInitialSalesOrderId(null);
          setDirectInitialCustomerId(null);
        }}
        onSuccess={(deliveryId) => {
          setDirectCreateModalOpen(false);
          setDirectInitialSalesOrderId(null);
          setDirectInitialCustomerId(null);
          navigate(`/growth/delivery-notes/${deliveryId}`);
        }}
      />
      {completeDelivery ? (
        <DeliveryCompletionModal
          open={!!completeDelivery}
          deliveryId={completeDelivery.id}
          deliveryNumber={completeDelivery.delivery_note_number}
          customerName={completeDelivery.customer?.customer_name}
          deliveryAddress={completeDelivery.delivery_address ?? null}
          instructions={completeDelivery.special_instructions ?? null}
          onClose={() => setCompleteDelivery(null)}
        />
      ) : null}
    </div>
  );
};

export default DeliveriesPage;
